import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Button,
  useMediaQuery,
} from "@mui/material";
import { rollout } from "../constants/rollout";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DealCard from "../components/deals/DealCard";
import {
  followButtonBlue,
  followButtonBlueHover,
} from "../constants/brandColors";
import { uiNeutrals } from "../constants/uiNeutrals";
import {
  DEALS_HEADER_TOP_GAP_PX,
  DEALS_HEADER_ROW_MIN_HEIGHT_PX,
  DEALS_HEADER_BOTTOM_PADDING_PX,
  dealsScrollContentPaddingTop,
} from "../constants/dealsShellLayout";
import { useRequireAuth } from "../hooks/useRequireAuth";
import { useAuth } from "../contexts/AuthContext";
import {
  followBrand,
  unfollowBrand,
  getFollowedBrands,
  isBrandFollowed,
} from "../utils/userPreferences";
import { useRestaurantDealsBySlug } from "../hooks/useRestaurantDealsBySlug";
import RestaurantDealCountPills from "../components/deals/RestaurantDealCountPills";
import RestaurantItemImage from "../components/restaurant/RestaurantItemImage";
import { formatBrandCuisineLine } from "../utils/formatBrandCuisineLine";

const RestaurantDeals = () => {
  const { restaurantName } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const requireAuth = useRequireAuth();
  const { isAuthenticated } = useAuth();
  const { deals, loading, restaurantInfo, activeDeals, inactiveDeals } =
    useRestaurantDealsBySlug(restaurantName);
  const [isFollowing, setIsFollowing] = useState(false);

  const n = uiNeutrals;

  const cuisineLine = restaurantInfo
    ? formatBrandCuisineLine(restaurantInfo.cuisine ?? restaurantInfo.category)
    : null;

  useEffect(() => {
    if (restaurantInfo?._id && isAuthenticated) {
      const checkFollowState = async () => {
        await getFollowedBrands();
        setIsFollowing(isBrandFollowed(restaurantInfo._id));
      };
      checkFollowState();
    } else {
      setIsFollowing(false);
    }
  }, [restaurantInfo?._id, isAuthenticated]);

  const handleFollowToggle = async () => {
    if (!restaurantInfo?._id) return;

    if (isFollowing) {
      const success = await unfollowBrand(restaurantInfo._id);
      if (success) {
        setIsFollowing(false);
        window.dispatchEvent(new Event("followedBrandsChanged"));
      }
      return;
    }

    requireAuth(async () => {
      const success = await followBrand(restaurantInfo._id);
      if (success) {
        setIsFollowing(true);
        window.dispatchEvent(new Event("followedBrandsChanged"));
      }
    });
  };

  const handleBack = () => {
    try {
      const cameFromSearch = sessionStorage.getItem("came_from_search");
      if (cameFromSearch === "true") {
        sessionStorage.removeItem("came_from_search");
        navigate("/deals");
        return;
      }
      const previousUrl = sessionStorage.getItem("deals_previous_url");
      if (previousUrl) {
        navigate(previousUrl);
        sessionStorage.removeItem("deals_previous_url");
      } else {
        navigate("/deals");
      }
    } catch (error) {
      navigate("/deals");
    }
  };

  const headerBackButtonSx = {
    width: 40,
    height: 40,
    p: 0,
    bgcolor: n.frostedFill,
    backdropFilter: "blur(18px) saturate(1.05)",
    WebkitBackdropFilter: "blur(18px) saturate(1.05)",
    border: n.frostedToggleBorder,
    borderRadius: "50%",
    color: "#000",
    "&:hover": { bgcolor: n.frostedFillHover },
  };

  if (loading) {
    return (
      <Box
        sx={{
          pt: dealsScrollContentPaddingTop,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress size={32} sx={{ color: "#000" }} />
      </Box>
    );
  }

  const seoName = restaurantInfo?.name || restaurantName;
  const seoTitle = `${seoName} Deals — Loops`;
  const seoDescription = cuisineLine
    ? `Browse ${seoName} food deals on Loops — ${cuisineLine} restaurant in Malaysia. Find discounted meals near you.`
    : `Browse ${seoName} food deals on Loops. Find discounted meals near you in Malaysia.`;
  const seoImage = restaurantInfo?.logoUrl || "https://loopsfood.com/assets/loops-og-image.png";
  const seoUrl = `https://loopsfood.com/${restaurantName}`;

  const restaurantJsonLd = restaurantInfo ? JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Restaurant",
    name: restaurantInfo.name,
    url: seoUrl,
    ...(restaurantInfo.logoUrl && { image: restaurantInfo.logoUrl, logo: restaurantInfo.logoUrl }),
    ...(cuisineLine && { servesCuisine: [
      ...(restaurantInfo.cuisine || []),
      ...(restaurantInfo.category || []),
    ].filter(Boolean) }),
    ...(restaurantInfo.address && { address: restaurantInfo.address }),
    areaServed: { "@type": "Country", name: "Malaysia" },
  }) : null;

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <link rel="canonical" href={seoUrl} />
        {restaurantJsonLd && (
          <script type="application/ld+json">{restaurantJsonLd}</script>
        )}
      </Helmet>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          pt: `calc(env(safe-area-inset-top, 0px) + ${DEALS_HEADER_TOP_GAP_PX}px)`,
          pb: `${DEALS_HEADER_BOTTOM_PADDING_PX}px`,
          bgcolor: "background.default",
          borderBottom: `1px solid ${n.rowDivider}`,
        }}
      >
        <Box
          sx={{
            minHeight: DEALS_HEADER_ROW_MIN_HEIGHT_PX,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            gap: 1,
          }}
        >
          <IconButton
            onClick={handleBack}
            aria-label="Back"
            sx={headerBackButtonSx}
          >
            <ArrowBackIcon sx={{ fontSize: 22 }} />
          </IconButton>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              flexShrink: 0,
            }}
          >
            {rollout.enableFollowing && restaurantInfo?._id ? (
              <Button
                variant="contained"
                size="small"
                disableRipple
                disableElevation
                onClick={handleFollowToggle}
                sx={{
                  minWidth: 80,
                  px: 1.5,
                  py: 1,
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "25px",
                  boxShadow: "none",
                  lineHeight: 1.2,
                  minHeight: 30,
                  ...(isFollowing
                    ? {
                        bgcolor: n.followingControl,
                        color: "#000",
                        "&:hover": { bgcolor: n.followingControlHover },
                      }
                    : {
                        bgcolor: followButtonBlue,
                        color: "#fff",
                        "&:hover": {
                          bgcolor: followButtonBlueHover,
                          borderColor: "rgba(0, 0, 0, 0.08)",
                        },
                      }),
                }}
              >
                {isFollowing ? "Following" : "Follow"}
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          minHeight: 0,
          pt: dealsScrollContentPaddingTop,
          "&::-webkit-scrollbar": {
            display: "none",
            width: 0,
            height: 0,
          },
          "&::-webkit-scrollbar-track": {
            display: "none",
          },
          "&::-webkit-scrollbar-thumb": {
            display: "none",
          },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            px: { xs: 2, md: 4 },
            pb: 4,
            pt: 3,
          }}
        >
          {restaurantInfo && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                py: 2,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  overflow: "hidden",
                  flexShrink: 0,
                  bgcolor: "background.default",
                  border: `1px solid ${n.wellBorder}`,
                }}
              >
                <RestaurantItemImage
                  restaurantName={restaurantInfo.name}
                  imageUrl={restaurantInfo.logoUrl}
                  useDefaultFallback
                  size="100%"
                  borderRadius="50%"
                  loading="eager"
                  sx={{ width: "100%", height: "100%" }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "1rem" }} noWrap>
                  {restaurantInfo.name}
                </Typography>
                {cuisineLine ? (
                  <Typography
                    sx={{ fontSize: "0.8rem", color: "#888", mt: 0.35 }}
                    noWrap
                    title={cuisineLine}
                  >
                    {cuisineLine}
                  </Typography>
                ) : null}
                {(activeDeals.length > 0 || inactiveDeals.length > 0) && (
                  <Box sx={{ mt: 1.25 }}>
                    <RestaurantDealCountPills
                      activeCount={activeDeals.length}
                      inactiveCount={inactiveDeals.length}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {activeDeals.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                sx={{
                  fontSize: { xs: "1rem", md: "1.05rem" },
                  color: "#1a1a1a",
                  mb: 2,
                }}
              >
                Active
              </Typography>
              <Grid container spacing={isMobile ? 2 : 3}>
                {activeDeals.map((deal) => (
                  <DealCard key={deal._id} deal={deal} isMobile={isMobile} />
                ))}
              </Grid>
            </Box>
          )}

          {inactiveDeals.length > 0 && (
            <Box sx={{ mt: activeDeals.length > 0 ? 4 : 0 }}>
              <Typography
                sx={{
                  fontSize: { xs: "1rem", md: "1.05rem" },
                  color: "#1a1a1a",
                  mb: 2,
                }}
              >
                Inactive
              </Typography>
              <Grid container spacing={isMobile ? 2 : 3}>
                {inactiveDeals.map((deal) => (
                  <DealCard key={deal._id} deal={deal} isMobile={isMobile} />
                ))}
              </Grid>
            </Box>
          )}

          {deals.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h5" color="text.secondary" gutterBottom>
                No deals found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No deals available for this restaurant.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RestaurantDeals;
